import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { GoogleButton } from "@/components/GoogleButton";

export default function Login() {
  const { signIn } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!email || !password) return;
    setBusy(true);
    try {
      const res = await api("/auth/login", {
        method: "POST",
        body: { email, password },
        auth: false,
      });
      await signIn(res.session_token, res.user);
      navigate("/");
    } catch (err) {
      toast.error(err.status === 401 ? "Invalid email or password" : "Could not sign in");
    } finally {
      setBusy(false);
    }
  };

  return (
    <AuthScaffold title="Welcome back" subtitle="Sign in to your family's BentoLife">
      <form onSubmit={submit} className="space-y-4">
        <div className="space-y-1.5">
          <Label htmlFor="email">Email</Label>
          <Input
            id="email"
            type="email"
            autoComplete="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="you@family.com"
            className="rounded-xl"
            data-testid="login-email-input"
          />
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="password">Password</Label>
          <Input
            id="password"
            type="password"
            autoComplete="current-password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder="••••••••"
            className="rounded-xl"
            data-testid="login-password-input"
          />
        </div>
        <Button type="submit" className="w-full rounded-xl" disabled={busy} data-testid="login-submit-btn">
          {busy ? "Signing in…" : "Sign in"}
        </Button>
      </form>
      <Divider />
      <GoogleButton testId="login-google-btn" label="Continue with Google" />
    </AuthScaffold>
  );
}

export function AuthScaffold({ title, subtitle, children }) {
  return (
    <div className="flex min-h-screen items-center justify-center bg-background px-4 py-10">
      <div className="w-full max-w-md">
        <Link to="/landing" className="mb-8 flex items-center justify-center gap-2.5">
          <img src="/logo.png" alt="BentoLife" className="h-11 w-11 rounded-xl border border-border" />
          <span className="font-display text-2xl font-semibold text-ink">BentoLife</span>
        </Link>
        <div className="bento-tile p-7">
          <h1 className="font-display text-2xl font-bold text-ink">{title}</h1>
          <p className="mb-6 mt-1 text-sm text-muted-foreground">{subtitle}</p>
          {children}
        </div>
      </div>
    </div>
  );
}

export function Divider() {
  return (
    <div className="my-5 flex items-center gap-3">
      <div className="h-px flex-1 bg-border" />
      <span className="text-xs font-semibold uppercase text-muted-foreground">or</span>
      <div className="h-px flex-1 bg-border" />
    </div>
  );
}
